// assistantPageStarters — the Page view's empty-workspace starter chips
// (WS-W2-ASSISTANT-PAGE-MODE-DOCK-1 unit (e); ASSISTANT_BOARD_MODE_SPEC §4
// "Empty workspace"; umbrella DESIGN-WL-ASSISTANT-MAIN-MODE-1).
//
// Rows for the shared AssistantStarterList (_simone/AssistantStarter.js —
// `{ key, text, prompt, icon, caption, localAction }`), built from the
// ai.boards VOCABULARY: the installed apps' widget catalogue the model is
// given (server src/domains/ai-boards, installed apps ∩ grants). Titles,
// describe lines and app names come from the apps' own manifests through the
// server — already in the member's language — so the chips carry NO copy of
// their own and always offer boards this workspace can actually build. One
// chip = one widget: the title is the capsule line, the describe line is the
// prompt that generates, the app's name is the caption, the widget's icon
// falls back to its app's. One widget per app before a second of any app,
// capped, deterministic.
//
// A tap generates that board directly (`assistantBoardGenerate` — the same
// leg homeAiSend's dashboard route takes, never the chat bridge); the main
// thread steps aside first because ONE view holds the main area. The shared
// capsule's own pre-open (`assistantOpen: true`) is converted by the shell's
// open edge under the main placement (app.js → promoteAssistantDockForOpen),
// so the drawer never opens on the way.
//
// The vocabulary is fetched ONCE per workspace, lazily, the first time a
// host asks (single-flight; a reply for a workspace the user has already
// left is dropped — the same guard as assistantBoardProbe). It lands in
// `_aiBoardVocab` (declared in state-app.js and wiped on a workspace switch
// beside `_aiBoardsAvailable`); a failure stores nothing so a later host may
// retry. The host mounts AssistantStarterList with these rows — the shared
// list and this feed are the whole surface, so the chat's starters and the
// Page view's can never drift apart.

const STARTER_CAP = 6

const activeWorkspaceId = (r) => {
  const ws = r && r.workspace
  return (ws && (ws.id || ws._id)) || null
}

// The rows. Pure read of `_aiBoardVocab`; [] until the vocabulary of the
// ACTIVE workspace has landed, so a reactive host simply re-renders when the
// load below writes the key.
export const assistantPageStarterRows = function assistantPageStarterRows(root) {
  const r = root || (this && this.state && this.state.root) || {}
  const vocab = r._aiBoardVocab
  if (!vocab || typeof vocab !== 'object') return []
  const wsId = activeWorkspaceId(r)
  if (wsId && vocab.workspaceId !== wsId) return []
  const widgets = Array.isArray(vocab.widgets) ? vocab.widgets : []
  if (!widgets.length) return []

  const apps = Array.isArray(vocab.apps) ? vocab.apps : []
  const appName = {}
  const appIcon = {}
  for (const a of apps) {
    if (!a || typeof a !== 'object') continue
    const k = a.app || a.appId
    if (!k) continue
    if (typeof a.name === 'string' && a.name) appName[k] = a.name
    if (typeof a.icon === 'string' && a.icon) appIcon[k] = a.icon
  }

  // One widget per app first (vocabulary order both ways), then fill.
  const byApp = new Map()
  for (const w of widgets) {
    if (!w || typeof w !== 'object' || typeof w.widget !== 'string' || !w.widget) continue
    const k = w.app || w.appId || ''
    const list = byApp.get(k) || []
    list.push(w)
    byApp.set(k, list)
  }
  const picked = []
  const seen = new Set()
  for (const [, list] of byApp) {
    if (picked.length >= STARTER_CAP) break
    picked.push(list[0])
    seen.add(list[0])
  }
  for (const [, list] of byApp) {
    if (picked.length >= STARTER_CAP) break
    for (const w of list) {
      if (picked.length >= STARTER_CAP) break
      if (seen.has(w)) continue
      picked.push(w)
      seen.add(w)
    }
  }

  return picked.map((w) => {
    const k = w.app || w.appId || ''
    const text = (typeof w.title === 'string' && w.title) || w.widget
    const prompt = (typeof w.describe === 'string' && w.describe) || text
    return {
      key: 'pgst-' + w.widget,
      text,
      prompt,
      icon: (typeof w.icon === 'string' && w.icon) || appIcon[k] || null,
      caption: appName[k] || null,
      localAction: 'assistantPageStarterSend'
    }
  })
}

// The lazy load. Call from the host's mount (or any opener of the empty
// surface): `el.call('assistantPageStartersLoad', s.root, el.call('getSDK'))`.
// Resolves true when a vocabulary for the ACTIVE workspace is on the root.
export const assistantPageStartersLoad = async function assistantPageStartersLoad(root, sdk) {
  const el = this
  const r = root || (el && el.state && el.state.root)
  if (!r || typeof r.update !== 'function') return false
  const workspaceId = activeWorkspaceId(r)
  const have = r._aiBoardVocab
  if (
    have &&
    typeof have === 'object' &&
    (!workspaceId || !have.workspaceId || have.workspaceId === workspaceId)
  ) {
    return true
  }
  const flight = workspaceId || 'none'
  if (r.__aiBoardVocabFor === flight) {
    // A load for this workspace is already in the air — share its landing.
    const req = r.__aiBoardVocabReq
    if (req && typeof req.then === 'function') return req
    return false
  }
  r.__aiBoardVocabFor = flight
  const run = (async () => {
    let res = null
    try {
      const s = sdk || (el && typeof el.call === 'function' ? el.call('getSDK') : null)
      if (!s || typeof s.execute !== 'function') throw new Error('unknown method: ai.boards')
      res = await s.execute('ai.boards', 'vocabulary', workspaceId ? { workspaceId } : {})
    } catch (_) {
      res = null
    }
    if (r.__aiBoardVocabFor === flight) {
      r.__aiBoardVocabFor = null
      r.__aiBoardVocabReq = null
    }
    const still = activeWorkspaceId(r)
    if (still !== workspaceId) return false
    if (!res || typeof res !== 'object') return false
    const vocab = res.workspaceId || !workspaceId ? res : { ...res, workspaceId }
    r.update({ _aiBoardVocab: vocab }, { preventFetch: true })
    return true
  })()
  r.__aiBoardVocabReq = run
  return run
}

// The chip's send — the row IS the capsule's state (AssistantStarterList is
// childrenAs:'state'). The shared capsule already flipped `assistantOpen`
// and the shell's open edge already converted that to the main thread under
// the main placement; the board takes the area from it, as in homeAiSend.
export const assistantPageStarterSend = function assistantPageStarterSend() {
  const el = this
  const s = el && el.state
  const raw = s && (s.prompt || s.text || s.label)
  const text = String(raw == null ? '' : raw).trim()
  if (!text) return null
  try {
    el.call('assistantMainThreadClose')
  } catch (_) {
    /* no thread view on this surface — nothing to step aside */
  }
  return el.call('assistantBoardGenerate', { prompt: text })
}
